import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DisplayOptions, Filter } from '../../models/filter-models';
import { GearSizeType } from "../../models/enums";
import { DisplayType } from "../../models/enums";
import { Rarity } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { FilterTitleComponent } from '../../common/filter-title/filter-title.component';
import { FilterService } from '../../services/filter.service';
import { UniquesTierService } from '../../services/tiering/uniques-tier.service';
import { ChanceBasesTierService } from '../../services/tiering/chance-bases-tier.service';
import { StylePickerComponent } from '../../common/style-picker/style-picker.component';
import { ItemDataService } from '../../services/item-data.service';
import { BaseComponent } from '../../common/base.component';
import { ActiveRules, ActiveSpecialRules } from '../../services/filter-generator.service';
import { SelectGearSizeComponent } from '../../common/select/select-gear-size/select-gear-size.component';
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { CustomRuleStylePickerComponent } from '../../common/style-picker/custom-rule-style-picker/custom-rule-style-picker.component';
import { NavigationService } from '../../services/navigation.service';
import { UniqueGeneralFiltersComponent } from './unique-general-filters/unique-general-filters.component';

@Component({
  selector: 'app-unique-filters',
  imports: [FormsModule, FilterTitleComponent, StylePickerComponent, SelectGearSizeComponent, DynamicContentComponent,
    CustomRuleStylePickerComponent, UniqueGeneralFiltersComponent
  ],
  templateUrl: './unique-filters.component.html',
  styleUrl: './unique-filters.component.scss'
})
export class UniqueFiltersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) activeRules!: ActiveRules;
  @Input({ required: true }) activeSpecialRules!: ActiveSpecialRules;

  firstExcellentUnique: string = '';
  firstGoodUnique: string = '';
  firstPotentialUnique: string = '';
  firstRestrictedUnique: string = '';
  firstOtherUnique: string = '';
  firstSearchingChanceBase: string = '';
  firstGoodChanceBase: string = '';

  isSpecificEndgameContentSelected = false;
  showFavouriteRestrictedBossUniquesToggle = false;
  showLowValueBossUniquesFilters = false;

  Rarity = Rarity;
  DisplayType = DisplayType;
  GearSizeType = GearSizeType;

  constructor(
    private filterService: FilterService,
    private uniquesTierService: UniquesTierService,
    private chanceBasesTierService: ChanceBasesTierService,
    private itemDataService: ItemDataService,
    private navigationService: NavigationService,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.constructUniqueData();
    }
  }

  onFavouriteEndgameContentChanged() {
    this.constructUniqueData();
  }

  private constructUniqueData() {
    if (!this.filter) return;

    this.firstSearchingChanceBase = this.chanceBasesTierService.getTieredChanceBases()['Searching'][0]?.baseType ?? 'Chance Base';
    this.firstGoodChanceBase = this.chanceBasesTierService.getTieredChanceBases()['Potential'][0]?.baseType ?? 'Chance Base';
    this.firstExcellentUnique = this.uniquesTierService.getTieredUniques()['Excellent'][0]?.baseType ?? 'Unique';
    this.firstGoodUnique = this.uniquesTierService.getTieredUniques()['Good'][0]?.baseType ?? 'Unique';
    this.firstPotentialUnique = this.uniquesTierService.getTieredUniques()['Potential'][0]?.baseType ?? 'Unique';
    this.firstRestrictedUnique = this.uniquesTierService.getTieredUniques()['Restricted'][0]?.baseType ?? 'Unique';
    this.firstOtherUnique = this.uniquesTierService.getTieredUniques()['Other'][0]?.baseType ?? 'Unique';
    this.updateEndgameBossUniquesUiState();
  }

  private updateEndgameBossUniquesUiState() {
    if (!this.filter) return;
    this.isSpecificEndgameContentSelected = !this.filter.favouriteEndgameContent.includes('All');
    const restrictedActive = this.filter.uniqueTierCosmetics[3].displayOptions.active;
    this.showFavouriteRestrictedBossUniquesToggle = this.isSpecificEndgameContentSelected && !restrictedActive;
    if (!this.filter.hideAllRemainingUniques) {
      this.showLowValueBossUniquesFilters = false;
    }
  }

  toggleHighlight(displayOptions: DisplayOptions) {
    displayOptions.active = !displayOptions.active;
  }

  toggleHighlightExcellentUniques = () => {
    this.toggleHighlight(this.filter.uniqueTierCosmetics[0].displayOptions);
    if (!this.filter.uniqueTierCosmetics[0].displayOptions.active) {
      this.filter.uniqueTierCosmetics[1].displayOptions.active = false;
      this.filter.uniqueTierCosmetics[2].displayOptions.active = false;
      this.filter.uniqueTierCosmetics[3].displayOptions.active = false;
      this.filter.uniqueTierCosmetics[4].displayOptions.active = false;
    }
    this.disableHideAllRemainingWhenAllUniquesAreEnabled();
    this.updateFilter();
  }

  toggleHighlightGoodUniques = () => {
    this.toggleHighlight(this.filter.uniqueTierCosmetics[1].displayOptions);
    if (!this.filter.uniqueTierCosmetics[1].displayOptions.active) {
      this.filter.uniqueTierCosmetics[2].displayOptions.active = false;
      this.filter.uniqueTierCosmetics[3].displayOptions.active = false;
      this.filter.uniqueTierCosmetics[4].displayOptions.active = false;
    }
    this.disableHideAllRemainingWhenAllUniquesAreEnabled();
    this.updateFilter();
  }

  toggleHighlightPotentialUniques = () => {
    this.toggleHighlight(this.filter.uniqueTierCosmetics[2].displayOptions);
    this.disableHideAllRemainingWhenAllUniquesAreEnabled();
    this.updateFilter();
  }

  toggleHighlightRestrictedUniques = () => {
    this.toggleHighlight(this.filter.uniqueTierCosmetics[3].displayOptions);
    this.disableHideAllRemainingWhenAllUniquesAreEnabled();
    this.updateEndgameBossUniquesUiState();
    this.updateFilter();
  }

  toggleShowFavouriteRestrictedBossUniques = () => {
    this.filter.showFavouriteRestrictedBossUniques = !this.filter.showFavouriteRestrictedBossUniques;
    this.updateFilter();
  }

  toggleShowFavouriteLowValueBossUniques = () => {
    this.filter.showFavouriteLowValueBossUniques = !this.filter.showFavouriteLowValueBossUniques;
    this.updateFilter();
  }

  toggleShowOtherLowValueBossUniques = () => {
    this.filter.showOtherLowValueBossUniques = !this.filter.showOtherLowValueBossUniques;
    this.updateFilter();
  }

  toggleHighlightOtherUniques = () => {
    this.toggleHighlight(this.filter.uniqueTierCosmetics[4].displayOptions);
    if (this.filter.uniqueTierCosmetics[4].displayOptions.active) {
      this.filter.uniqueTierCosmetics[0].displayOptions.active = true;
      this.filter.uniqueTierCosmetics[1].displayOptions.active = true;
      this.filter.uniqueTierCosmetics[2].displayOptions.active = true;
      this.filter.uniqueTierCosmetics[3].displayOptions.active = true;
    }
    this.disableHideAllRemainingWhenAllUniquesAreEnabled();
    this.updateFilter();
  }

  toggleHighlightExceptionalOtherUniques = () => {
    this.filter.showExceptionalUniques = !this.filter.showExceptionalUniques;
    this.updateFilter();
  }

  toggleHighlightSmallOtherUniques = () => {
    this.filter.showSmallUniques = !this.filter.showSmallUniques;
    this.updateFilter();
  }

  toggleHighlightFoulbornUniques = () => {
    this.filter.showFoulbornUniques = !this.filter.showFoulbornUniques;
    this.updateFilter();
  }

  toggleHighlightUniqueMaps = () => {
    this.filter.showUniqueMaps = !this.filter.showUniqueMaps;
    this.updateFilter();
  }

  toggleHighlightVaalUniques = () => {
    this.filter.showVaalUniques = !this.filter.showVaalUniques;
    this.updateFilter();
  }

  toggleHighlightVaalModUniques = () => {
    this.filter.showVaalModUniques = !this.filter.showVaalModUniques;
    this.updateFilter();
  }

  toggleHighlightHighDustValueUniques = () => {
    this.filter.showHighDustValueUniques = !this.filter.showHighDustValueUniques;
    this.updateFilter();
  }

  toggleHideAllRemainingUniques = () => {
    this.filter.hideAllRemainingUniques = !this.filter.hideAllRemainingUniques;
    if (!this.filter.hideAllRemainingUniques) {
      this.showLowValueBossUniquesFilters = false;
    }
    this.updateEndgameBossUniquesUiState();
    this.updateFilter();
  }

  disableHideAllRemainingWhenAllUniquesAreEnabled = () => {
    if (this.filter.uniqueTierCosmetics[0].displayOptions.active
      && this.filter.uniqueTierCosmetics[1].displayOptions.active
      && this.filter.uniqueTierCosmetics[2].displayOptions.active
      && this.filter.uniqueTierCosmetics[3].displayOptions.active
      && this.filter.uniqueTierCosmetics[4].displayOptions.active) {
      this.filter.hideAllRemainingUniques = false;
    }
  }

  toggleHighlightExcellentChanceBases = () => {
    this.toggleHighlight(this.filter.chanceBaseTierCosmetics[0].displayOptions);
    if (!this.filter.chanceBaseTierCosmetics[0].displayOptions.active) {
      this.filter.chanceBaseTierCosmetics[1].displayOptions.active = false;
    }
    this.updateFilter();
  }

  toggleHighlightGoodChanceBases = () => {
    this.toggleHighlight(this.filter.chanceBaseTierCosmetics[1].displayOptions);
    this.updateFilter();
  }

  rebuildTierList() {
    this.uniquesTierService.combineTieredUniques(this.filter);
    this.chanceBasesTierService.combineTieredChanceBases(this.filter);
    this.filterService.updateFilter();
    this.constructUniqueData();
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  navigateToOtherUniques = () => {
    this.navigationService.navigateToFilterTitle('uniques-other');
  }

  navigateToFavouriteRestrictedBossUniques = () => {
    this.navigationService.navigateToFilterTitle('uniques-restricted-favourite-bosses');
  }

  navigateToRestrictedUniques = () => {
    this.navigationService.navigateToFilterTitle('uniques-restricted');
  }
}
